import React, { useState, useEffect, useContext } from 'react';
import { useAppLanguage } from '../../../Hooks/useAppLanguage';
import AuthContext from '../../../Context/AuthContext';
import { useToast } from '../../../Context/ToastContext';
import { ordersAPI } from '../../../Api/api';
import Button from '../../../Component/UI/Button/Button';
import CustomDropdown from '../../../Component/UI/CustomDropdown/CustomDropdown';
import ProductCardImage from '../../../Component/UI/ProductCard/ProductCardImage';
import {
    canCancelProductOrder,
    formatOrderItemVariantLabel,
    formatOrderMoney,
    getProductOrderStatusClass,
    resolveOrderItemName,
} from '../../../Utils/orderDisplay';
import './MyOrders.css';
import { getApiBaseUrl } from '../../../lib/storefront/storeBootstrap';

// ZATCA tax invoice page (served by the API); the backend link when it knows its public URL.
const invoiceLink = (invoice) =>
    invoice.url || `${getApiBaseUrl()}/api/public/invoices/${invoice.publicToken}`;

const MyOrders = () => {
    const { t, currentLanguage, dir, langPath } = useAppLanguage();
    const { user } = useContext(AuthContext);
    const { addToast } = useToast();
    const showTrackOrdersLinks = (process.env.REACT_APP_SHOW_TRACK_ORDERS_LINKS || 'true').toLowerCase() === 'true';

    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [cancellingOrderId, setCancellingOrderId] = useState(null);

    const statusOptions = [
        { value: 'all', label: t('myOrders.allOrders') },
        { value: 'pending', label: t('myOrders.statusPending') },
        { value: 'confirmed', label: t('myOrders.statusConfirmed') },
        { value: 'processing', label: t('myOrders.statusProcessing') },
        { value: 'shipped', label: t('myOrders.statusShipped') },
        { value: 'delivered', label: t('myOrders.statusDelivered') },
        { value: 'completed', label: t('myOrders.statusCompleted') },
        { value: 'cancelled', label: t('myOrders.statusCancelled') },
    ];

    const getStatusText = (status) => {
        const key = status?.toLowerCase?.();
        const map = {
            'awaiting-payment': t('myOrders.statusAwaitingPayment'),
            pending: t('myOrders.statusPending'),
            confirmed: t('myOrders.statusConfirmed'),
            processing: t('myOrders.statusProcessing'),
            shipped: t('myOrders.statusShipped'),
            delivered: t('myOrders.statusDelivered'),
            completed: t('myOrders.statusCompleted'),
            cancelled: t('myOrders.statusCancelled'),
            refunded: t('myOrders.statusRefunded'),
        };
        return map[key] || status;
    };

    const getPaymentMethodLabel = (method) => {
        const key = method?.toLowerCase?.();
        const map = {
            cash: t('orderConfirmation.paymentCash'),
            card: t('orderConfirmation.paymentCard'),
            'bank-transfer': t('orderConfirmation.paymentBankTransfer'),
            online: t('orderConfirmation.paymentOnline'),
            wallet: t('orderConfirmation.paymentWallet'),
        };
        return map[key] || method || '—';
    };

    useEffect(() => {
        const loadOrders = async () => {
            if (!user?._id) {
                setError(t('myOrders.loginRequired'));
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await ordersAPI.getUserOrders();
                if (response?.data?.status === 'success') {
                    const data = response.data.data || [];
                    setOrders(data);
                    setFilteredOrders(data);
                } else {
                    setError(response?.data?.message || t('myOrders.loadError'));
                }
            } catch (err) {
                setError(err?.response?.data?.message || err?.message || t('myOrders.loadError'));
            } finally {
                setLoading(false);
            }
        };

        loadOrders();
    }, [user, t]);

    useEffect(() => {
        if (statusFilter === 'all') {
            setFilteredOrders(orders);
        } else {
            setFilteredOrders(orders.filter((order) => order.orderStatus === statusFilter));
        }
    }, [orders, statusFilter]);

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm(t('myOrders.confirmCancel'))) {
            return;
        }

        try {
            setCancellingOrderId(orderId);
            const response = await ordersAPI.cancelUserOrder(orderId);
            if (response?.data?.status === 'success') {
                const updateList = (prev) =>
                    prev.map((order) =>
                        order._id === orderId
                            ? { ...order, orderStatus: 'cancelled', cancelledAt: new Date() }
                            : order
                    );
                setOrders(updateList);
                setFilteredOrders(updateList);
                if (selectedOrder?._id === orderId) {
                    setSelectedOrder(null);
                }
                addToast(t('myOrders.cancelSuccess'), 'success');
            } else {
                addToast(response?.data?.message || t('myOrders.cancelError'), 'error');
            }
        } catch (err) {
            addToast(err?.response?.data?.message || err?.message || t('myOrders.cancelError'), 'error');
        } finally {
            setCancellingOrderId(null);
        }
    };

    if (!user) {
        return (
            <main className="main">
                <section className="my-orders-page">
                    <div className="container" dir={dir}>
                        <div className="my-orders-login-card">
                            <h2 className="my-orders-login-title">{t('myOrders.loginRequired')}</h2>
                            <p className="my-orders-login-text">{t('myOrders.loginMessage')}</p>
                            <Button to={langPath('/sign-in')} flex>
                                {t('myOrders.signIn')}
                            </Button>
                        </div>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="main">
            <section className="my-orders-page">
                <div className="container" dir={dir}>
                    <div className="my-orders-filter">
                        <span className="my-orders-filter-label">{t('myOrders.filterByStatus')}</span>
                        <CustomDropdown
                            options={statusOptions}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            placeholder={t('myOrders.allOrders')}
                        />
                    </div>

                    {error && (
                        <div className="my-orders-error" role="alert">
                            <p>{error}</p>
                        </div>
                    )}

                    {loading ? (
                        <div className="my-orders-loading">
                            <div className="my-orders-spinner" aria-hidden />
                            <p>{t('myOrders.loading')}</p>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="my-orders-empty">
                            <h2 className="my-orders-empty-title">{t('myOrders.noOrders')}</h2>
                            <p className="my-orders-empty-text">{t('myOrders.noOrdersMessage')}</p>
                            <Button to={langPath('/products')} flex>
                                {t('myOrders.startOrdering')}
                            </Button>
                        </div>
                    ) : (
                        <div className="my-orders-grid">
                            {filteredOrders.map((order) => (
                                <article key={order._id} className="my-orders-card">
                                    <div className="my-orders-card-head">
                                        <div className="my-orders-card-number">#{order.orderNumber}</div>
                                        <div className={`my-orders-status ${getProductOrderStatusClass(order.orderStatus)}`}>
                                            {getStatusText(order.orderStatus)}
                                        </div>
                                    </div>

                                    <div className="my-orders-card-meta">
                                        <div className="my-orders-meta-block">
                                            <span className="my-orders-meta-label">{t('myOrders.orderDate')}</span>
                                            <span className="my-orders-meta-value">
                                                {new Date(order.createdAt).toLocaleDateString(currentLanguage)}
                                            </span>
                                        </div>
                                        <div className="my-orders-meta-block">
                                            <span className="my-orders-meta-label">{t('myOrders.total')}</span>
                                            <span className="my-orders-meta-value my-orders-meta-value--accent">
                                                {formatOrderMoney(order.totalAmount, order.currency)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="my-orders-preview">
                                        <h3 className="my-orders-preview-title">{t('myOrders.items')}</h3>
                                        <div className="my-orders-preview-list">
                                            {order.items?.slice(0, 3).map((item, idx) => (
                                                <div key={idx} className="my-orders-preview-row">
                                                    <span className="my-orders-preview-name">
                                                        {resolveOrderItemName(item.name, currentLanguage)}
                                                    </span>
                                                    <span className="my-orders-preview-qty">×{item.quantity}</span>
                                                </div>
                                            ))}
                                            {order.items?.length > 3 && (
                                                <div className="my-orders-preview-more">
                                                    +{order.items.length - 3} {t('myOrders.moreItems')}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="my-orders-card-actions">
                                        <Button
                                            variant="outline"
                                            type="button"
                                            size="sm"
                                            onClick={() =>
                                                setSelectedOrder(selectedOrder?._id === order._id ? null : order)
                                            }
                                        >
                                            {selectedOrder?._id === order._id
                                                ? t('myOrders.hideDetails')
                                                : t('myOrders.viewDetails')}
                                        </Button>
                                        {canCancelProductOrder(order.orderStatus) && (
                                            <Button
                                                variant="danger"
                                                type="button"
                                                size="sm"
                                                onClick={() => handleCancelOrder(order._id)}
                                                disabled={cancellingOrderId === order._id}
                                            >
                                                {cancellingOrderId === order._id
                                                    ? t('myOrders.cancelling')
                                                    : t('myOrders.cancelOrder')}
                                            </Button>
                                        )}
                                        {showTrackOrdersLinks && (
                                            <Button
                                                to={langPath(`/track-order?orderNumber=${order.orderNumber}`)}
                                                flex
                                                size="sm"
                                            >
                                                {t('myOrders.trackOrder')}
                                            </Button>
                                        )}
                                        {order.invoice && (
                                            <Button
                                                variant="outline"
                                                href={invoiceLink(order.invoice)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                flex
                                                size="sm"
                                            >
                                                {t('myOrders.viewInvoice')}
                                            </Button>
                                        )}
                                    </div>

                                    {selectedOrder?._id === order._id && (
                                        <div className="my-orders-detail">
                                            <div className="my-orders-detail-section">
                                                <h3 className="my-orders-detail-heading">{t('myOrders.orderSummary')}</h3>
                                                <div className="my-orders-summary-grid">
                                                    <div className="my-orders-summary-cell">
                                                        <span className="my-orders-summary-label">
                                                            {t('myOrders.paymentMethod')}
                                                        </span>
                                                        <span className="my-orders-summary-value">
                                                            {getPaymentMethodLabel(order.payment?.paymentMethod)}
                                                        </span>
                                                    </div>
                                                    <div className="my-orders-summary-cell">
                                                        <span className="my-orders-summary-label">
                                                            {t('myOrders.paymentStatus')}
                                                        </span>
                                                        <span className="my-orders-summary-value">
                                                            {order.payment?.paymentStatus || '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="my-orders-detail-section my-orders-detail-items">
                                                <h3 className="my-orders-detail-heading">{t('myOrders.allItems')}</h3>
                                                {order.items?.map((item, idx) => {
                                                    const variantLabel = formatOrderItemVariantLabel(item);
                                                    return (
                                                    <div key={idx} className="my-orders-line">
                                                        <div className="my-orders-line-info">
                                                            <div className="my-orders-line-thumb">
                                                                <ProductCardImage
                                                                    product={{
                                                                        _id: item.productId,
                                                                        imageUrl: item.primaryImage,
                                                                        primaryImage: item.primaryImage,
                                                                    }}
                                                                    productName={resolveOrderItemName(item.name, currentLanguage)}
                                                                    alt={resolveOrderItemName(item.name, currentLanguage)}
                                                                    className="my-orders-line-img"
                                                                    compact
                                                                />
                                                            </div>
                                                            <div>
                                                                <div className="my-orders-line-name">
                                                                    {resolveOrderItemName(item.name, currentLanguage)}
                                                                </div>
                                                                {variantLabel ? (
                                                                    <div>
                                                                        {variantLabel}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <span className="my-orders-line-qty">×{item.quantity}</span>
                                                        <span className="my-orders-line-price">
                                                            {formatOrderMoney(item.price, order.currency)}
                                                        </span>
                                                    </div>
                                                    );
                                                })}
                                            </div>

                                            {order.customerInfo?.shippingAddress && (
                                                <div className="my-orders-detail-section">
                                                    <h3 className="my-orders-detail-heading">
                                                        {t('myOrders.shippingAddress')}
                                                    </h3>
                                                    <p className="my-orders-address">
                                                        {order.customerInfo.shippingAddress.street}
                                                        {order.customerInfo.shippingAddress.apartment
                                                            ? `, ${order.customerInfo.shippingAddress.apartment}`
                                                            : ''}
                                                        <br />
                                                        {[
                                                            order.customerInfo.shippingAddress.city,
                                                            order.customerInfo.shippingAddress.state,
                                                            order.customerInfo.shippingAddress.postalCode,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                        {order.customerInfo.shippingAddress.country ? (
                                                            <>
                                                                <br />
                                                                {order.customerInfo.shippingAddress.country}
                                                            </>
                                                        ) : null}
                                                    </p>
                                                </div>
                                            )}

                                            {order.customerNotes && (
                                                <div className="my-orders-detail-section">
                                                    <h3 className="my-orders-detail-heading">
                                                        {t('myOrders.specialInstructions')}
                                                    </h3>
                                                    <p className="my-orders-notes">{order.customerNotes}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}

                    {showTrackOrdersLinks && filteredOrders.length > 0 && (
                        <div className="my-orders-footer-actions">
                            <Button to={langPath('/track-order')} variant="outline" flex>
                                {t('myOrders.trackAnotherOrder')}
                            </Button>
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
};

export default MyOrders;
